import { useState, useEffect, useRef, type ReactNode } from "react"
import BaseEditor, {
  type BeforeMount,
  type OnMount,
} from "@monaco-editor/react"
import type { editor } from "monaco-editor"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Button from "@/v2-backoffice-client/components/button"
import { useEditorSettings } from "@/v2-backoffice-client/components/editor/editor.settings"

// Font size and line height come from the operator's editor settings.
const EDITOR_OPTIONS: editor.IStandaloneEditorConstructionOptions = {
  minimap: { enabled: false },
  lineNumbers: "on",
  scrollBeyondLastLine: false,
  wordWrap: "on",
  formatOnPaste: true,
  automaticLayout: true,
  tabSize: 2,
  padding: { top: 8, bottom: 8 },
}

export type EditorProps = {
  className?: string
  value: string
  language?: string
  /** Monaco model URI; lets a registered JSON schema target this editor. */
  path?: string
  /** Runs before the editor mounts, e.g. to register JSON schemas. */
  beforeMount?: BeforeMount
  /** Exposes the Monaco editor instance (e.g. for the inspector lens). */
  onMount?: OnMount
  /** Observes the draft text as the user types (e.g. for live diagnostics). */
  onChange?(value: string): void
  onSave(value: string): void
  isSaving?: boolean
  /** The save button's label; the create flow renames it "Create layer". */
  saveLabel?: ReactNode
  /**
   * Keeps the save button enabled with an unchanged document — the create
   * flow may submit the seeded starter configuration as-is.
   */
  canSaveClean?: boolean
  /**
   * Extra buttons for the editor toolbar (e.g. the workbench's panel
   * toggles), rendered as their own group between Format and Save.
   */
  toolbarExtras?: ReactNode
  /**
   * Floats over the code area (which is position: relative), e.g. the
   * workbench's diagnostics indicator.
   */
  overlay?: ReactNode
}

function Editor(props: EditorProps) {
  const {
    value,
    language = "json",
    path,
    beforeMount,
    onMount,
    onChange,
    onSave,
    isSaving,
    saveLabel = "Save",
    canSaveClean,
    toolbarExtras,
    overlay,
    className,
  } = props
  const [text, setText] = useState(value)
  const [error, setError] = useState<string | null>(null)
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null)
  const { monacoTheme, fontSize, lineHeight } = useEditorSettings()

  useEffect(() => {
    setText(value)
    setError(null)
  }, [value])

  const handleEditorMount: OnMount = (editorInstance, monaco) => {
    editorRef.current = editorInstance
    onMount?.(editorInstance, monaco)
  }

  // Undo/redo availability comes straight from Monaco's own history.
  // Every keystroke (and every undo/redo itself) fires onChange, so the
  // component re-renders exactly when these can flip.
  const model = editorRef.current?.getModel()
  const canUndo = model?.canUndo() ?? false
  const canRedo = model?.canRedo() ?? false

  /**
   * Replaces the whole document through the editor's edit pipeline
   * (never setValue) so the change lands on the undo stack — Reset and
   * Format are themselves undoable with Cmd/Ctrl+Z.
   */
  function replaceText(source: string, nextText: string) {
    const editorInstance = editorRef.current
    const editorModel = editorInstance?.getModel()
    if (!editorInstance || !editorModel) return
    editorInstance.pushUndoStop()
    editorInstance.executeEdits(source, [
      { range: editorModel.getFullModelRange(), text: nextText },
    ])
    editorInstance.pushUndoStop()
  }

  function runHistoryAction(action: "undo" | "redo") {
    const editorInstance = editorRef.current
    if (!editorInstance) return
    editorInstance.trigger("editor-actions", action, null)
    editorInstance.focus()
  }

  return (
    <div className={cn("editor", className)}>
      <div className="editor-area">
        <BaseEditor
          value={text}
          defaultLanguage={language}
          path={path}
          theme={monacoTheme}
          beforeMount={beforeMount}
          onMount={handleEditorMount}
          options={{ ...EDITOR_OPTIONS, fontSize, lineHeight }}
          onChange={(v) => {
            setText(v ?? "")
            setError(null)
            onChange?.(v ?? "")
          }}
        />
        {overlay}
      </div>

      {error && <p className="editor-error">{error}</p>}

      <div className="editor-actions" role="toolbar" aria-label="Editor">
        {/* History group */}
        <Button
          variant="ghost"
          size="sm"
          iconSrc="undo-alt"
          title="Undo (Cmd/Ctrl+Z)"
          aria-label="Undo"
          disabled={!canUndo}
          onClick={() => runHistoryAction("undo")}
        />
        <Button
          variant="ghost"
          size="sm"
          iconSrc="redo-alt"
          title="Redo (Cmd/Ctrl+Shift+Z)"
          aria-label="Redo"
          disabled={!canRedo}
          onClick={() => runHistoryAction("redo")}
        />
        <Button
          variant="ghost"
          size="sm"
          iconSrc="time-past"
          title="Discard the draft and restore the last saved value"
          aria-label="Reset to the saved value"
          disabled={text === value}
          onClick={() => {
            replaceText("editor-reset", value)
            setError(null)
          }}
        />

        <Separator orientation="vertical" className="h-5" />

        {/* Document group */}
        <Button
          variant="ghost"
          size="sm"
          iconSrc="brackets-curly"
          title="Pretty-print the draft (undoable)"
          aria-label="Format the draft"
          disabled={text === value}
          onClick={() => {
            const formatted = (() => {
              try {
                return JSON.stringify(JSON.parse(text), null, 2)
              } catch {
                return null
              }
            })()

            if (formatted) {
              replaceText("editor-format", formatted)
            } else {
              setError("Cannot format: invalid input")
            }
          }}
        />

        <span className="flex-1" />

        {/* Panels group (owner-provided, e.g. inspector / AI assistant) */}
        {toolbarExtras && (
          <>
            {toolbarExtras}
            <Separator orientation="vertical" className="h-5" />
          </>
        )}

        <Button
          size="sm"
          isLoading={isSaving}
          disabled={!canSaveClean && text === value}
          onClick={() => {
            try {
              JSON.parse(text)
              setError(null)
              onSave(text)
            } catch (error) {
              setError(error instanceof Error ? error.message : "Invalid input")
            }
          }}
        >
          {saveLabel}
        </Button>
      </div>
    </div>
  )
}

export default Editor
